import { useIntl } from "react-intl";
import { useHistory, useLocation } from "react-router-dom";
import { makeStyles } from "@mui/styles";
import { useTheme, Box, Tabs, Tab } from "@mui/material";

import { Header, SettingsMenu } from "@mb-pro-ui/components";
import { ReactComponent as LogoSvg } from "../../icons/logo.svg";

import { useAuth } from "../../utils/AuthProvider";
import { useMenuButtonContainer } from "./MenuButtonContainerProvider";
import { appVersion } from "../..";
import { useMenuLinks } from "../../hooks";
import { useCallback, useEffect, useState } from "react";

const useLogoStyles = makeStyles(() => {
  const theme = useTheme();

  return {
    logo: {
      "& .secondary": {
        color: theme.palette.secondary.main,
      },
    },
  };
});

export default function AppHeader() {
  const [value, setValue] = useState<number | false>(false);

  const auth = useAuth();
  const { formatMessage } = useIntl();
  const history = useHistory();
  const location = useLocation();

  const { logo } = useLogoStyles();
  const { containerRef } = useMenuButtonContainer();
  const menulinks = useMenuLinks();

  const navigateToPath = useCallback(
    ({ path, newValue, newTab }: { path?: string; newValue?: number; newTab?: boolean }) => {
      if (!path && newValue === undefined) {
        return;
      }
      if (!path) {
        path = menulinks[newValue as number].href;
      }
      if (newValue === undefined) {
        newValue = menulinks.findIndex(({ href }) => href === path);
        if (newValue === -1) {
          return;
        }
      }
      if (newTab) {
        window.open(path, "_blank", "noopener,noreferrer");
      } else {
        if (path === '/configuration') {
          path += sessionStorage.getItem("last_device_filter");
          sessionStorage.removeItem("last_device_filter");
        }
        setValue(newValue);
        history.push(path);
      }
    },
    [history, menulinks]
  );

  const handleChange = useCallback(
    (event: React.SyntheticEvent, newValue: number) => {
      const mouseEvent =
        (event as unknown as MouseEvent).ctrlKey !== undefined && (event as unknown as MouseEvent).metaKey !== undefined
          ? (event as unknown as MouseEvent)
          : null;

      navigateToPath({ newValue, newTab: (mouseEvent && (mouseEvent.ctrlKey || mouseEvent.metaKey)) ?? false });
    },
    [navigateToPath]
  );

  const onTabClick = useCallback(
    (path: string): React.MouseEventHandler<HTMLAnchorElement> =>
      (mouseEvent) => {
        if (location.pathname !== path && !location.pathname.startsWith(path)) {
          return;
        }
        navigateToPath({ path, newTab: (mouseEvent.ctrlKey || mouseEvent.metaKey) ?? false });
      },
    [location.pathname, navigateToPath]
  );

  useEffect(() => {
    const menuValue = menulinks.findIndex(({ href }) => href === location.pathname || `${location.pathname}/`.startsWith(`${href}/`));
    setValue(menuValue < 0 ? false : menuValue);
  }, [location, menulinks]);

  return (
    <Header
      position="fixed"
      sx={{
        zIndex: (theme) => theme.zIndex.drawer + 3,
        paddingLeft: "10px",
        paddingRight: "10px",
        height: { xs: 66, sm: 72 },
      }}
    >
      <Box ref={containerRef} sx={{ alignSelf: "center" }} />
      <Tabs
        value={value}
        onChange={handleChange}
        sx={{
          "& .MuiTab-root": {
            color: "primary.contrastText",
            fontWeight: "normal",
          },
          "& .MuiTabs-indicator": {
            backgroundColor: "primary.contrastText",
          },
        }}
        variant="scrollable"
        scrollButtons="auto"
      >
        {menulinks.map(({ icon, href, label }) => (
          <Tab
            key={`menu-${href}`}
            icon={icon}
            label={label}
            component="a"
            sx={{
              "&.Mui-selected": {
                color: "primary.contrastText",
              },
            }}
            onClick={onTabClick(href)}
          />
        ))}
      </Tabs>
      {auth?.user?.name && (
        <SettingsMenu
          settingsItemProps={{ onClick: () => history.push("/settings") }}
          onClickLogout={() => auth.signout.mutate()}
          settingsMessage={formatMessage({
            defaultMessage: "Settings",
            description: "App header settings button label",
          })}
          logoutMessage={formatMessage({
            defaultMessage: "Logout",
          })}
          icon={<LogoSvg className={logo} />}
          userTitle={formatMessage({
            defaultMessage: "Logged in",
          })}
          userName={auth.user.fullname ?? auth.user.name}
          appVersion={appVersion}
        />
      )}
    </Header>
  );
}
